import { useState, useEffect, useCallback, useRef } from 'react'
import {
	Box,
	Typography,
	useMediaQuery,
	useTheme
} from '@mui/material'
import { supabase } from "../utils/supabase"
import {
	getCalendarDaysView,
	setCalendarDaysView,
	getCalendarSelectedEmployeeIds,
	setCalendarSelectedEmployeeIds,
	getMobileCalendarViewMode,
	setMobileCalendarViewMode
} from "../utils/userPreferenceUtils"
import { getCurrencySymbol } from '../utils/currencyUtils'
import AppointmentDetailsDialog from './AppointmentDetailsDialog'
import { AddAppointmentDialog } from './add-appointment'
import CalendarMobile from './calendar/CalendarMobile'
import CalendarToolbar from './calendar/CalendarToolbar'
import CalendarTimeGrid from './calendar/CalendarTimeGrid'
import CalendarMonthGrid from './calendar/CalendarMonthGrid'
import { buildVisits, serviceColorSet, CAL_HOUR_H, CAL_WORK_START } from './calendar/calShared'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

function CalendarView({ merchant, onOpenSidebar, onOpenClientProfile, onOpenPetProfile, onOpenInvoice }) {
	// Always initialize calendar to today to ensure we don't show past dates by default
	const [calendarDate, setCalendarDate] = useState(() => dayjs())
	const [calendarAppointments, setCalendarAppointments] = useState([])
	const [loadingCalendarAppointments, setLoadingCalendarAppointments] = useState(false)
	const [selectedAppointment, setSelectedAppointment] = useState(null)
	const [appointmentDetailsOpen, setAppointmentDetailsOpen] = useState(false)
	const [addAppointmentOpen, setAddAppointmentOpen] = useState(false)
	const [prefilledDateTime, setPrefilledDateTime] = useState(null)
	const addAppointmentDialogRef = useRef(null)
	const [employees, setEmployees] = useState([])
	const [selectedEmployees, setSelectedEmployees] = useState([])
	const [employeesLoaded, setEmployeesLoaded] = useState(false) // Track if employees have been loaded
	const [mobileViewMode, setMobileViewModeState] = useState(() => getMobileCalendarViewMode('list')) // 'list' | 'grid' | 'month'
	const [desktopDaysView, setDesktopDaysView] = useState(() => getCalendarDaysView(5)) // Load from localStorage

	// Wrapper to persist mobile view mode to localStorage
	const setMobileViewMode = useCallback((mode) => {
		setMobileViewModeState(mode)
		setMobileCalendarViewMode(mode)
	}, [])
	const [currentTime, setCurrentTime] = useState(dayjs())
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// AppBar height constant (standard MUI AppBar height)
	// const appBarHeight = 64 // Temporarily unused

	// Update current time every minute to refresh the red line position
	useEffect(() => {
		const updateTime = () => {
			setCurrentTime(dayjs())
		}

		// Update immediately
		updateTime()

		// Update every minute
		const interval = setInterval(updateTime, 60000)

		return () => clearInterval(interval)
	}, [])

	const loadEmployees = useCallback(async () => {
		if (!merchant?.id) return

		try {
			const { data, error } = await supabase
				.from('employees')
				.select('id, first_name, last_name')
				.eq('merchant_id', merchant.id)
				.eq('active', true)
				.order('first_name')

			if (error) throw error

			setEmployees(data || [])
			setEmployeesLoaded(true)
		} catch (e) {
			console.error('Error loading employees:', e)
			setEmployees([])
			setEmployeesLoaded(true)
		}
	}, [merchant?.id])

	const loadCalendarAppointments = useCallback(async () => {
		if (!merchant?.id) return

		setLoadingCalendarAppointments(true)
		try {
			let startDate, endDate

			if (isMobile) {
				// Mobile loads the full month padded to week boundaries so the
				// week strip and month grids always have data for visible days
				startDate = calendarDate.startOf('month').startOf('week')
				endDate = calendarDate.endOf('month').endOf('week')
			} else if (desktopDaysView === 'month') {
				const startOfMonth = calendarDate.startOf('month')
				const endOfMonth = calendarDate.endOf('month')
				startDate = startOfMonth.startOf('day')
				endDate = endOfMonth.endOf('day')
			} else {
				const daysToShow = desktopDaysView
				startDate = calendarDate.startOf('day')
				endDate = calendarDate.add(daysToShow - 1, 'day').endOf('day')
			}

			let query = supabase
				.from('appointments')
				.select(`
				id,
				start_ts,
				end_ts,
				status,
				customer_name,
				phone_number,
				user_id,
				pet_id,
				comments,
				employee_id,
				service_id,
				group_id,
				add_ons,
				service_price,
				service_types(name, duration_minutes, appointment_color, price),
				employees(first_name, last_name, email),
				pets(name, breed)
			`)
				.eq('merchant_id', merchant.id)
				.not('status', 'eq', 'cancelled')
				.gte('start_ts', startDate.utc().toISOString())
				.lte('start_ts', endDate.utc().toISOString())

			// Apply employee filter if selected
			if (selectedEmployees.length > 0) {
				const employeeIds = selectedEmployees.map(emp => emp.id)
				query = query.in('employee_id', employeeIds)
			}

			const { data, error } = await query.order('start_ts', { ascending: true })

			if (error) throw error

			const appointmentsWithNames = (data || []).map(appointment => ({
				...appointment,
				customerName: appointment.customer_name || 'Customer'
			}))

			setCalendarAppointments(appointmentsWithNames)
		} catch (e) {
			console.error('Error loading calendar appointments:', e)
			setCalendarAppointments([])
		} finally {
			setLoadingCalendarAppointments(false)
		}
	}, [merchant?.id, calendarDate, isMobile, selectedEmployees, desktopDaysView])

	// Load employees when merchant changes
	useEffect(() => {
		if (merchant?.id) {
			loadEmployees()
		}
	}, [merchant?.id, loadEmployees])

	// Restore selected employees from localStorage after employees are loaded
	useEffect(() => {
		if (employeesLoaded && employees.length > 0 && merchant?.id) {
			const savedEmployeeIds = getCalendarSelectedEmployeeIds(merchant.id)
			if (savedEmployeeIds.length > 0) {
				// Filter to only include employees that still exist
				const restoredEmployees = employees.filter(emp =>
					savedEmployeeIds.includes(emp.id)
				)
				setSelectedEmployees(restoredEmployees)
			}
		}
	}, [employeesLoaded, employees, merchant?.id])

	// Load calendar appointments when date changes
	useEffect(() => {
		if (merchant?.id) {
			loadCalendarAppointments()
		}
	}, [calendarDate, merchant?.id, loadCalendarAppointments])

	// Auto-scroll on initial load (desktop grid): jump to current time if today
	// is in view, otherwise put the store open time at the top. Jump instantly
	// (no smooth animation) so the calendar doesn't visibly scroll on load.
	// The mobile timeline handles its own initial scroll in CalendarMobile.
	useEffect(() => {
		if (isMobile) return
		const scrollToInitialPosition = () => {
			const scrollContainer = document.querySelector('[data-calendar-scroll]')
			if (!scrollContainer) return

			const now = dayjs()
			// Today may be visible even when calendarDate isn't today (multi-day views).
			const viewStart = calendarDate.startOf('day')
			const viewEnd = calendarDate.add(desktopDaysView, 'day').startOf('day')
			const isTodayInView = !now.isBefore(viewStart) && now.isBefore(viewEnd)

			// Scroll with a small delay to ensure the grid is rendered.
			setTimeout(() => {
				// Sticky header height, so the target row lands just below it.
				const headerOffset = 54

				let scrollTop
				if (isTodayInView) {
					// Position the current-time line at 1/3 from the top for visibility.
					const scrollPosition = (now.hour() + now.minute() / 60) * CAL_HOUR_H + headerOffset
					const offsetFromTop = scrollContainer.clientHeight / 3
					scrollTop = scrollPosition - offsetFromTop
				} else {
					// Put store open time at the very top of the viewport.
					scrollTop = CAL_WORK_START * CAL_HOUR_H
				}

				// Temporarily disable smooth scrolling so the jump is instant.
				const prevBehavior = scrollContainer.style.scrollBehavior
				scrollContainer.style.scrollBehavior = 'auto'
				scrollContainer.scrollTop = Math.max(0, scrollTop)
				scrollContainer.style.scrollBehavior = prevBehavior
			}, 100)
		}

		scrollToInitialPosition()
	}, [calendarDate, loadingCalendarAppointments, desktopDaysView, isMobile])

	const generateCalendarDays = () => {
		const daysToShow = desktopDaysView
		const days = []
		for (let i = 0; i < daysToShow; i++) {
			const day = calendarDate.add(i, 'day')
			days.push({
				date: day,
				dayName: day.format('ddd'),
				dayNumber: day.format('D'),
				monthName: day.format('MMM')
			})
		}
		return days
	}

	// Get the employees to display columns for
	const getDisplayEmployees = () => {
		if (selectedEmployees.length === 0) {
			// Show all employees when none selected
			return employees
		}
		return selectedEmployees
	}

	// Handle clicking on empty calendar slots (mins = minutes since midnight).
	// In the day view (lanes mode) each column belongs to an employee, so we
	// seed that employee into the add-appointment flow — it gets applied at the
	// end if that employee can perform the chosen service (matches mobile).
	const handleGridSlotClick = (day, mins, employeeId = null) => {
		const date = day.format('YYYY-MM-DD')
		const time = dayjs().hour(Math.floor(mins / 60)).minute(mins % 60).format('HH:mm')
		const employee = employeeId ? employees.find(e => e.id === employeeId) || null : null

		if (addAppointmentOpen && addAppointmentDialogRef.current) {
			// Dialog is already open — update start time only, preserve all other state
			addAppointmentDialogRef.current.updateStartDateTime(date, time)
		} else {
			handleAddAppointment({ date, time, employee })
		}
	}

	const handleAppointmentClick = (appointment) => {
		setSelectedAppointment(appointment)
		setAppointmentDetailsOpen(true)
	}

	const handleCloseAppointmentDetails = () => {
		setAppointmentDetailsOpen(false)
		setSelectedAppointment(null)
	}

	const handleAddAppointment = (prefilledData = null) => {
		setPrefilledDateTime(prefilledData)
		setAddAppointmentOpen(true)
	}

	const handleCloseAddAppointment = () => {
		setAddAppointmentOpen(false)
		setPrefilledDateTime(null)
	}

	const handleAppointmentCreated = (newAppointment) => {
		// Reload calendar appointments to show the new appointment
		loadCalendarAppointments()
	}

	const handleAppointmentUpdated = (updatedAppointment) => {
		// Reload calendar appointments to reflect the updated appointment
		loadCalendarAppointments()
	}

	const handleEmployeeToggle = (employee) => {
		const currentIndex = selectedEmployees.findIndex(e => e.id === employee.id)
		const newSelected = [...selectedEmployees]

		if (currentIndex === -1) {
			newSelected.push(employee)
		} else {
			newSelected.splice(currentIndex, 1)
		}

		setSelectedEmployees(newSelected)
		// Save to localStorage
		setCalendarSelectedEmployeeIds(merchant?.id, newSelected.map(e => e.id))
	}

	// Clear the employee filter (the "All" chip)
	const handleClearEmployeeFilter = () => {
		setSelectedEmployees([])
		setCalendarSelectedEmployeeIds(merchant?.id, [])
	}

	// Get appointments for a specific day
	const getAppointmentsForDay = (day) => {
		const tz = merchant?.timezone || 'UTC'
		const dayStart = day.startOf('day')
		const dayEnd = day.endOf('day')

		return calendarAppointments.filter(appointment => {
			const appointmentStart = dayjs(appointment.start_ts).tz(tz)
			const appointmentEnd = dayjs(appointment.end_ts).tz(tz)

			// Check if appointment overlaps with this day
			const overlapsDay = appointmentStart.isBefore(dayEnd) && appointmentEnd.isAfter(dayStart)
			return overlapsDay
		}).sort((a, b) => dayjs(a.start_ts).diff(dayjs(b.start_ts)))
	}


	// ── desktop redesign helpers (Claude Design "Calendar · five views") ──
	const tz = merchant?.timezone || 'UTC'
	const nowTz = currentTime.tz(tz)
	const cs = getCurrencySymbol(merchant?.currency)
	const formatPrice = (cents) => `${cs}${((cents || 0) / 100).toFixed(2)}`

	// Visits for one grid column. Day view passes an employeeId (lane per
	// groomer); multi-day views pass null and merge every displayed employee
	// into the day column. Rows sharing a group_id merge into one multi-pet
	// visit card.
	const getVisitsForGrid = (day, employeeId) => {
		const dayAppointments = getAppointmentsForDay(day)
		const filtered = employeeId
			? dayAppointments.filter(a => a.employee_id === employeeId)
			: dayAppointments
		return buildVisits(filtered, tz, { groupByEmployee: Boolean(employeeId), formatPrice })
	}

	// Jump into Day view for a specific date (month day numbers, cluster cards)
	const handleOpenDay = (day) => {
		setCalendarDate(day)
		setDesktopDaysView(1)
		setCalendarDaysView(1)
	}

	// Service color legend built from the services visible in the loaded range
	const buildLegend = () => {
		const seen = new Set()
		const items = []
		let hasMulti = false
		calendarAppointments.forEach(a => {
			if (a.group_id) hasMulti = true
			const name = a.service_types?.name
			if (name && !seen.has(name)) {
				seen.add(name)
				items.push({ name, accent: serviceColorSet(a.service_types?.appointment_color).accent })
			}
		})
		return { items: items.slice(0, 6), extra: Math.max(0, items.length - 6), hasMulti }
	}


	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
			{/* Mobile View — Claude Design "Mobile · calendar" */}
			{isMobile ? (
				<CalendarMobile
					merchant={merchant}
					now={nowTz}
					calendarDate={calendarDate}
					onDateChange={setCalendarDate}
					mode={mobileViewMode}
					onModeChange={setMobileViewMode}
					employees={employees}
					selectedEmployees={selectedEmployees}
					onToggleEmployee={handleEmployeeToggle}
					onClearEmployeeFilter={handleClearEmployeeFilter}
					getVisits={getVisitsForGrid}
					loading={loadingCalendarAppointments}
					onVisitClick={(visit) => handleAppointmentClick(visit.appointment)}
					onAddAppointment={handleAddAppointment}
					onOpenSidebar={onOpenSidebar}
				/>
			) : (
				/* Desktop View — Claude Design "Calendar · five views" */
				(() => {
					const legend = buildLegend()
					const displayEmployees = getDisplayEmployees()
					const days = generateCalendarDays().map(d => d.date)
					return (
						<>
							{/* Toolbar */}
							<CalendarToolbar
								view={desktopDaysView}
								onViewChange={(v) => {
									setDesktopDaysView(v)
									setCalendarDaysView(v)
								}}
								anchor={calendarDate}
								onAnchorChange={setCalendarDate}
								employees={employees}
								selectedEmployees={selectedEmployees}
								onToggleEmployee={handleEmployeeToggle}
								onClearEmployeeFilter={handleClearEmployeeFilter}
								onAddAppointment={() => handleAddAppointment(null)}
							/>

							{/* Service color legend */}
							{legend.items.length > 0 && (
								<Box sx={{
									display: 'flex', alignItems: 'center', gap: '15px', p: '0 22px 10px',
									flex: 'none', flexWrap: 'wrap', fontFamily: "'Outfit', sans-serif",
								}}>
									{legend.items.map(item => (
										<Box key={item.name} component="span" sx={{
											display: 'inline-flex', alignItems: 'center', gap: '6px',
											fontSize: '11px', fontWeight: 700, color: '#8B85A4',
										}}>
											<Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', flex: 'none', bgcolor: item.accent }} />
											{item.name}
										</Box>
									))}
									{legend.extra > 0 && (
										<Box component="span" sx={{ fontSize: '11px', fontWeight: 700, color: '#8B85A4' }}>
											{`+${legend.extra} more`}
										</Box>
									)}
									{legend.hasMulti && (
										<>
											<Box component="span" sx={{ width: '1px', height: 13, bgcolor: '#E7E2F3' }} />
											<Box component="span" sx={{
												display: 'inline-flex', alignItems: 'center', gap: '6px',
												fontSize: '11px', fontWeight: 700, color: '#8B85A4',
											}}>
												<Box component="span" sx={{ display: 'flex', flex: 'none' }}>
													<Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', border: '1.5px solid #FBFAFE', bgcolor: '#7C6BD9' }} />
													<Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', border: '1.5px solid #FBFAFE', bgcolor: '#3E9BD0', ml: '-3px' }} />
												</Box>
												Multi-pet visit
											</Box>
										</>
									)}
								</Box>
							)}

							{/* Calendar Content with Side Panel */}
							<Box sx={{
								display: 'flex',
								flex: 1,
								minHeight: 0,
								overflow: 'hidden',
								width: '100%',
								gap: addAppointmentOpen ? '10px' : 0,
								p: '0 20px 18px',
								boxSizing: 'border-box',
							}}>
								{/* Calendar Grid Container */}
								<Box sx={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', transition: 'all 0.3s ease' }}>
									{loadingCalendarAppointments ? (
										<Box sx={{
											display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1,
											bgcolor: '#FFFFFF', border: '1px solid #E7E2F3', borderRadius: '14px',
										}}>
											<Typography sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '13px', fontWeight: 650, color: '#8B85A4' }}>
												Loading calendar…
											</Typography>
										</Box>
									) : desktopDaysView === 'month' ? (
										<CalendarMonthGrid
											anchor={calendarDate}
											now={nowTz}
											visitsForDay={(day) => getVisitsForGrid(day, null)}
											onOpenDay={handleOpenDay}
											onVisitClick={(visit) => handleAppointmentClick(visit.appointment)}
										/>
									) : desktopDaysView === 1 ? (
										<CalendarTimeGrid
											mode="lanes"
											merchant={merchant}
											date={calendarDate}
											employees={displayEmployees}
											getVisits={getVisitsForGrid}
											now={nowTz}
											onSlotClick={handleGridSlotClick}
											onVisitClick={(visit) => handleAppointmentClick(visit.appointment)}
											onOpenDay={handleOpenDay}
										/>
									) : (
										<CalendarTimeGrid
											mode="days"
											merchant={merchant}
											days={days}
											getVisits={getVisitsForGrid}
											now={nowTz}
											onSlotClick={handleGridSlotClick}
											onVisitClick={(visit) => handleAppointmentClick(visit.appointment)}
											onOpenDay={handleOpenDay}
										/>
									)}
								</Box>

								{/* Desktop Side Panel for Add Appointment */}
								{addAppointmentOpen && (
									<Box sx={{
										width: 370,
										flexShrink: 0,
										height: '100%',
										overflow: 'hidden'
									}}>
										<AddAppointmentDialog
											ref={addAppointmentDialogRef}
											open={addAppointmentOpen}
											onClose={handleCloseAddAppointment}
											merchant={merchant}
											onAppointmentCreated={handleAppointmentCreated}
											prefilledDateTime={prefilledDateTime}
											prefilledAppointment={prefilledDateTime?.employee ? {
												date: prefilledDateTime.date,
												time: prefilledDateTime.time,
												employee: prefilledDateTime.employee,
											} : null}
										/>
									</Box>
								)}
							</Box>
						</>
					)
				})()
			)}

		{/* Mobile Add Appointment Dialog (drawer) */}
		{isMobile && (
			<AddAppointmentDialog
				ref={addAppointmentDialogRef}
				open={addAppointmentOpen}
				onClose={handleCloseAddAppointment}
				merchant={merchant}
				onAppointmentCreated={handleAppointmentCreated}
				prefilledDateTime={prefilledDateTime}
				prefilledAppointment={prefilledDateTime?.employee ? {
					date: prefilledDateTime.date,
					time: prefilledDateTime.time,
					employee: prefilledDateTime.employee,
				} : null}
			/>
		)}

			{/* Appointment Details Dialog */}
			<AppointmentDetailsDialog
				open={appointmentDetailsOpen}
				onClose={handleCloseAppointmentDetails}
				appointment={selectedAppointment}
				merchant={merchant}
				onAppointmentUpdated={handleAppointmentUpdated}
				onOpenClientProfile={onOpenClientProfile}
				onOpenPetProfile={onOpenPetProfile}
				onOpenInvoice={onOpenInvoice}
			/>
		</Box>
	)
}

export default CalendarView
